/**
@file Este arquivo router.js eh responsavel por declarar todas as rotas do cliente. Atraves do VueRouter, podemos declarar rotas, adicionar redirecionamentos e apelidar nossas rotas atraves da propriedade 'name'. Também existem as guardas de navegação, onde antes de entrar numa rota, um certo código é executado para garantir a integridade dos dados ou até fornecer os dados necessários pra rota.

@author Chris
*/

import Vue from 'vue'
import Router from 'vue-router'
import call from '@/api/calls'
import LogRocket from 'logrocket'

Vue.use(Router)

let routeHistory = [];
let isProduction = process.env.NODE_ENV == 'production'
// isProduction = true

//  Aqui nós declaramos as rotas. Mais informações em https://router.vuejs.org/guide/
export default function createRouter () {
  let router = new Router({
    mode: 'history',
    routes: [
      {
        path: '',
        name: 'root',
        redirect: '/landing'
      },
      {
        path: '/home',
        name: 'home',
        component: () => import('./components/pages/home.vue'),
        meta: {
          requiresAuth: isProduction,
          analytics: {
            pageviewTemplate(route) {
              return {
                title: "Home",
                page: route.path,
                location: window.location.origin
              }
            }
          }
        }
      },
      {
        path: '/landing',
        name: 'landing',
        component: () => import('./components/pages/landing.vue')
      },
      {
        path: '/feedback/',
        name: 'feedback',
        component: () => import('./components/pages/userFeedback.vue'),
      },
      {
        path: '/tell_us/',
        name: 'tell_us',
        component: () => import('./components/pages/tellUs.vue'),
      },
      {
        path: '/c/:id([A-Za-z0-9_-]{26})',
        name: 'chapter',
        component: () => import('./components/pages/chapter.vue'),
      },
      {
        path: '/search',
        name: 'search',
        component: () => import('./components/pages/search.vue'),
      },
      {
        path: '/session_expired/',
        name: 'sessionExpired',
        redirect: 'logout',
      },
      {
        path: '/refresh_session/',
        name: 'refreshSession',
        meta: {
          requiresServerAuth: isProduction
        }
      },
      {
        path: '/login/',
        name: 'login',
        redirect: 'landing'
      },
      {
        path: '/logged/',
        name: 'logged',
        meta: {
          requiresServerAuth: isProduction
        }
      },
      {
        path: '/logout/',
        name: 'logout',
        beforeEnter: (to, from, next) => {
          if (localStorage.getItem('user')) {
            // remove local session:
            localStorage.removeItem('user')
          }
          // and remove server session and redirect to home (server-side)
          call.auth.logout()
          .then(() => next({ name: 'landing' }))
          .catch(() => {
            next({ name: 'landing' });
          })
        }
      },
      {
        path: '/p/:id([A-Za-z0-9_-]{18})',
        name: 'profile',
        component: () => import('./components/pages/profile.vue'),
        meta: {
          requiresAuth: isProduction
        }
      },
      {
        path: '/g/:id([A-Za-z0-9_-]{26})',
        name: 'place',
        component: () => import('./components/pages/place.vue'),
      },
      {
        path: '*',
        name: 'notFound',
        component: () => import('./components/pages/notFound404.vue')
      },
      {
        path: '/about',
        name: 'about',
        component: () => import('./components/content/footer/about.vue'),
      },
      {
        path: '/privacy',
        name: 'privacy',
        component: () => import('./components/content/footer/privacyTerms.vue'),
      },
      {
        path: '/terms',
        name: 'terms',
        component: () => import('./components/content/footer/termsUse.vue'),
      },
      {
        path: '/beginning',
        name: 'beginning',
        component: () => import('./components/content/footer/beginning.vue'),
      },
    ]
  })

  // Authenticate flow
  router.beforeEach((to, from, next) => {
    // if(!isProduction)
    //   // eslint-disable-next-line no-console
    //   console.log(`from ${from.name} to ${to.name}`);

    if (from.name == null)  routeHistory = [ { name: to.name, path: to.path } ];
    else  routeHistory[routeHistory.length] = { name: to.name, path: to.path };
    to.params.history = routeHistory;
    to.params.isProduction = isProduction;

    // Checks for user ttl
    let user = JSON.parse(localStorage.getItem('user'))
    if (user != null) {
      // last update exceeded TTL?
      // 604800000 = 7 * 24 * 60 * 60 * 1000 (1 week)
      if (Math.abs(new Date() - new Date(user.updatedAt) >= 604800000)) {
        // remove client session
        localStorage.removeItem('user')
      }
      else {
        LogRocket.identify(user.id, {
          name: user.fullName || user.name,
          email: user.email,
          session: user
        })
      }
    }

    if(to.matched.some(record => record.meta.requiresAuth || record.meta.requiresServerAuth)) {
      // If route requires server authentication, ...
      if (to.matched.some(record => record.meta.requiresServerAuth)) {
        // check server for user session, then ...
        call.auth.getSessionUser()
        .then(userSession => {
          // if has user session, ...
          if (userSession) {
            // authenticate client:
            localStorage.setItem('user', JSON.stringify(userSession))
            if (to.name == 'refreshSession') {
              // when used with $router.push(), must use catch or will throw error.
              next(false)
            }
            // redirect to home if recently logged or go on
            else if (to.name == 'logged') {
              let route = localStorage.getItem('loginCallRoute');
              if (route) {
                localStorage.removeItem('loginCallRoute');
                next(route);
              }
              else {
                next({ name: 'home' });
              }
            }
            else {
              next()
            }
          }
          // if server session not found, ...
          else {
            // remove local storage and send to login page
            localStorage.removeItem('user')
            to.name == 'landing' ? next() : next({ name: 'logout' })
          }
        })
        .catch(error => {
          LogRocket.captureException(error);
          next({ name: 'sessionExpired' });
        })
      }
      // If requires client authentication, ...
      if (to.matched.some(record => record.meta.requiresAuth )) {
        // if cookie not empty, continue...
        if (localStorage.getItem('user'))
          next();
        // if cookie was removed in this cicle, direct to session expired
        else if (user)
          next({ name: 'sessionExpired' });
        // if cookie and user are null, direct to login
        else
          next({ name: 'login' });
      }
    }
    else next()
  })

  return router
}
